import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import { setupI18n } from './locales/i18n';
import { subscribeAuthState, initAuthSessionState } from '@/utils/auth-session';
import { getUserId } from '@/utils/auth-token';
import { resetAllFeedStores } from '@/utils/reset-feed-stores';
import { getFeedScrollDebugSnapshot } from '@/composables/useFeedScrollContext.js';
import '@fontsource-variable/inter/opsz.css';
import '@fontsource/noto-sans-sc/400.css';
import '@fontsource/noto-sans-sc/500.css';
import '@fontsource/noto-sans-sc/600.css';
import '@fontsource/noto-sans-sc/700.css';
import { setupAnalytics } from '@/utils/analytics-bootstrap';
import './assets/styles/base.scss';
import 'element-plus/theme-chalk/dark/css-vars.css';
import 'element-plus/es/components/message/style/css';
import './assets/styles/element-h5-overrides.scss';
import './assets/styles/sheet-overrides.scss';
import message from '@/utils/message';
import './registerServiceWorker';
import { ensurePwaInstallListeners } from '@/composables/usePwaInstall';
import { lockPortraitOrientation } from '@/utils/lock-portrait';
import { probeAppBuildAtBoot, startServiceWorkerUpdateChecks } from '@/utils/app-build-sync';

// 尽早监听 beforeinstallprompt，避免 async 安装横幅挂载前错过事件
ensurePwaInstallListeners();
lockPortraitOrientation();

async function boot() {
  // 生产：仅用户主动打开/刷新时探测旧壳；命中才硬换壳，使用中不 reload
  const probed = await probeAppBuildAtBoot();
  if (probed.reloading) return;
  startServiceWorkerUpdateChecks();

  try {
    const i18n = await setupI18n();
    const app = createApp(App);
    const pinia = createPinia();
    app.use(pinia);
    app.use(router);
    app.use(i18n);
    app.config.globalProperties.$message = message;

    setupAnalytics(router);

    if (process.env.NODE_ENV === 'development') {
      window.__dsPinia = pinia;
      window.__dsFeedScrollDebug = getFeedScrollDebugSnapshot;
    }

    /**
     * 登出 / 401：performLocalLogout → runAuthTeardown（含 watchlist reset）。
     * 换号登录：全量 reset feed stores。
     */
    let wasLoggedIn = initAuthSessionState();
    let lastFeedUserId = wasLoggedIn ? getUserId() || '' : '';
    subscribeAuthState((loggedIn) => {
      if (wasLoggedIn && !loggedIn) {
        lastFeedUserId = '';
      } else if (loggedIn) {
        const userId = getUserId() || '';
        if (lastFeedUserId && userId && lastFeedUserId !== userId) {
          resetAllFeedStores();
        }
        lastFeedUserId = userId;
      }
      wasLoggedIn = loggedIn;
    });

    // 等初始路由完成再挂载，避免 /pwa 等页先闪首页+登录前弹窗
    await router.isReady();
    app.mount('#app');
    if (typeof window !== 'undefined' && typeof window.__DS_HIDE_BOOT_SPLASH__ === 'function') {
      window.__DS_HIDE_BOOT_SPLASH__();
    }
  } catch (err) {
    console.error('[boot] setupI18n failed', err);
    if (typeof window !== 'undefined' && typeof window.__DS_HIDE_BOOT_SPLASH__ === 'function') {
      window.__DS_HIDE_BOOT_SPLASH__();
    }
  }
}

void boot();
